import { useMemo, useState, useEffect, useRef } from 'react';

import { Section } from '@snippets';
import { calcResponsiveHeight, withInView } from '@utils';
import { Box, Container, Flex, Text, Image } from 'theme-ui';
import { padding } from '@settings/common';

import { themed } from './HeroValueProps.theme';
import { Schema } from './HeroValueProps.schema';

function SocialProofAnimation({ socialProof }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentNumber, setCurrentNumber] = useState(
    socialProof?.baseNumber || 0
  );
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    if (!socialProof?.enabled || !socialProof?.items?.length) {
      return undefined;
    }

    // Initialize visible items
    setVisibleItems(socialProof.items.slice(0, 4));

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextIndex = (prev + 1) % socialProof.items.length;

        // Create new visible items array with the next item sliding in from left
        setVisibleItems((prevItems) => {
          const newItems = [
            socialProof.items[nextIndex],
            ...prevItems.slice(0, 3),
          ];
          return newItems;
        });

        return nextIndex;
      });
      setCurrentNumber((prev) => prev + 1);
    }, 2500);

    return () => clearInterval(interval);
  }, [socialProof]);

  if (!socialProof?.enabled || !socialProof?.items?.length) return null;

  return (
    <Flex sx={{ alignItems: 'center', gap: '12px', mt: 4 }}>
      <Box
        sx={{
          position: 'relative',
          width: '94px', // Adjusted for 4 images at 34px with -12px overlap
          height: '38px',
          overflow: 'visible',
        }}
      >
        <Flex
          sx={{
            alignItems: 'center',
            position: 'absolute',
            right: 0,
            transition: 'transform 0.5s ease-in-out',
            transform: 'translateX(0)',
          }}
        >
          {visibleItems.map((item, index) => (
            <Box
              key={`${item.image?.alt || 'User'}-${index}-${currentIndex}`}
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                overflow: 'hidden',
                position: 'relative',
                marginLeft: index > 0 ? '-12px' : 0,
                zIndex: index + 1,
                flexShrink: 0,
                border: `2px solid ${socialProof.ringColor || '#ffffff'}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                animation:
                  index === 0
                    ? 'slideIn 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                    : 'none',
                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                '@keyframes slideIn': {
                  '0%': {
                    transform: 'translateX(-46px) scale(0.8)',
                    opacity: 0,
                  },
                  '60%': {
                    transform: 'translateX(-10px) scale(0.95)',
                    opacity: 0.7,
                  },
                  '100%': {
                    transform: 'translateX(0) scale(1)',
                    opacity: 1,
                  },
                },
              }}
            >
              {(() => {
                const imageSrc =
                  item.image?.src?.src ||
                  item.image?.src?.url ||
                  item.image?.src ||
                  item.image;
                return imageSrc ? (
                  <Image
                    src={imageSrc}
                    alt={item.image?.alt || 'User'}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(135deg, ${
                        ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD'][
                          index % 5
                        ]
                      } 0%, ${
                        ['#FF8E53', '#44A08D', '#5C7CFA', '#FECA57', '#F093FB'][
                          index % 5
                        ]
                      } 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: 'white',
                      textTransform: 'uppercase',
                    }}
                  >
                    {(item.image?.alt || 'User').charAt(0)}
                  </Box>
                );
              })()}
            </Box>
          ))}
        </Flex>
      </Box>
      <Flex
        sx={{
          alignItems: 'center',
          borderRadius: '50px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          height: '38px',
          border: '1px solid #fff',
        }}
      >
        <Box
          sx={{
            bg: 'white',
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: '100%',
          }}
        >
          <Text
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              minWidth: '70px',
              justifyContent: 'center',
            }}
          >
            <span>⭐</span>
            {currentNumber.toLocaleString()}+
          </Text>
        </Box>
        <Box
          sx={{
            bg: '#fff',
            opacity: 0.5,
            px: 3,
            pr: 4,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Text
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: 'black',
              letterSpacing: '0.01em',
            }}
          >
            {socialProof.label}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

function ValueProps({ valueProps, setValuePropsHeight }) {
  const valuePropsRef = useRef(null);

  useEffect(() => {
    if (valuePropsRef?.current?.offsetHeight) {
      setValuePropsHeight(valuePropsRef.current.offsetHeight / 2);
    }
  }, [valuePropsRef?.current?.offsetHeight]);

  if (!valueProps?.enabled || !valueProps?.items?.length) return null;

  return (
    <Box
      sx={{
        mt: [0, 5],
        position: ['absolute', 'relative'],
        bottom: ['-16px', 'auto'],
        transform: ['translateY(50%)', 'none'],
        width: '100%',
      }}
      ref={valuePropsRef}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: ['repeat(2, 1fr)', 'repeat(4, 1fr)'],
          gap: [3, 6],
          bg: valueProps.backgroundColor || 'rgba(0,0,0,0.8)',
          p: [3, 4],
        }}
      >
        {valueProps.items.map((item, index) => (
          <Flex
            key={index}
            sx={{
              p: 4,
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              color: valueProps?.textColor || 'white',
            }}
          >
            <Box sx={{ mb: 2 }}>
              {(() => {
                const iconSrc =
                  item.icon?.src?.src ||
                  item.icon?.src?.url ||
                  item.icon?.src ||
                  item.icon;
                return iconSrc ? (
                  <Image
                    src={iconSrc}
                    alt={item.icon?.alt || item.title}
                    sx={{ width: 24, height: 24 }}
                  />
                ) : null;
              })()}
            </Box>
            <Text sx={{ fontSize: [1, 2], fontWeight: 600, mb: 1 }}>
              {item.title}
            </Text>
            <Text
              sx={{
                fontSize: [0, 1],
                opacity: 0.8,
                display: ['none', 'block'],
              }}
            >
              {item.description}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}

export const HeroValueProps = withInView(
  themed(({ theme, cms }) => {
    const { backgroundImage, text, socialProof, valueProps, section } = cms;
    const [valuePropsHeight, setValuePropsHeight] = useState(0);

    const height = useMemo(() => {
      return [
        calcResponsiveHeight({
          maxHeight: section?.mobile?.maxHeight,
          viewport: 768,
        }),
        calcResponsiveHeight({
          maxHeight: section?.desktop?.maxHeight,
          viewport: 1280,
        }),
      ];
    }, [section?.mobile?.maxHeight, section?.desktop?.maxHeight]);

    const backgroundImageSrc = useMemo(() => {
      const src = [];
      if (backgroundImage?.mobile?.src?.src) {
        src.push(backgroundImage.mobile.src.src);
      }
      if (backgroundImage?.desktop?.src?.src) {
        src.push(backgroundImage.desktop.src.src);
      }
      return src;
    }, [backgroundImage]);

    // Get padding values from the schema
    const pyMobile = padding?.value?.[section?.heroPadding?.mobile?.py] || [
      0, 0, 0, 0,
    ];
    const pxMobile = padding?.value?.[section?.heroPadding?.mobile?.px] || [
      0, 0, 0, 0,
    ];
    const pyDesktop = padding?.value?.[section?.heroPadding?.desktop?.py] || [
      0, 0, 0, 0,
    ];
    const pxDesktop = padding?.value?.[section?.heroPadding?.desktop?.px] || [
      0, 0, 0, 0,
    ];

    return (
      <Section section={section} suppressHydrationWarning>
        <Container
          data-comp={HeroValueProps.displayName}
          sx={{
            ...theme.heroValueProps,
            height,
            minHeight: [
              `${section?.mobile?.minHeight}px`,
              `${section?.desktop?.minHeight}px`,
            ],
            maxHeight: [
              `${section?.mobile?.maxHeight}px`,
              `${section?.desktop?.maxHeight}px`,
            ],
            backgroundImage: backgroundImageSrc.map(
              (src) => src && `url(${src})`
            ),
            backgroundSize: 'cover',
            backgroundPosition: [
              backgroundImage?.mobile?.position || 'center',
              backgroundImage?.desktop?.position || 'center',
            ],
            backgroundColor: section?.bgColor || 'transparent',
            py: [
              pyMobile?.[0] || 0,
              pyDesktop?.[1] || 0,
              pyDesktop?.[2] || null,
              pyDesktop?.[3] || null,
            ],
            px: [
              pxMobile?.[0] || 0,
              pxDesktop?.[1] || 0,
              pxDesktop?.[2] || null,
              pxDesktop?.[3] || null,
            ],
            overflow: ['visible', 'hidden'],
            marginBottom: [`${valuePropsHeight}px`, 0],
          }}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Flex
              sx={{
                height: '100%',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  maxWidth: ['100%', '50%'],
                  textAlign: 'left',
                  color: text?.color || 'white',
                }}
              >
                {text?.logo?.src && (
                  <Box
                    sx={{
                      mb: 6,
                    }}
                  >
                    <Image
                      src={text.logo.src}
                      alt={text.logo?.altText || 'logo'}
                      width="36"
                      height="36"
                      className="w-[36px]"
                    />
                  </Box>
                )}

                {text?.superheading && (
                  <Text
                    sx={{
                      fontSize: [1, 2],
                      fontWeight: 400,
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {text.superheading}
                  </Text>
                )}

                {text?.heading && (
                  <Text
                    as="h1"
                    variant={text.headingOption || 'text.h1'}
                    sx={{
                      fontSize: [
                        `${
                          text.mobileFontSize === 'custom'
                            ? text.customMobileFontSize || 44
                            : text.mobileFontSize || 44
                        }px`,
                        `${
                          text.desktopFontSize === 'custom'
                            ? text.customDesktopFontSize || 84
                            : text.desktopFontSize || 84
                        }px`,
                      ],
                      lineHeight: ['1.10', '1.10'],
                      mb: 3,
                    }}
                  >
                    {text.heading}
                  </Text>
                )}

                {text?.subheading && (
                  <Text
                    sx={{
                      fontSize: [2, 3],
                      fontWeight: 400,
                      mb: 3,
                      opacity: 0.9,
                    }}
                  >
                    {text.subheading}
                  </Text>
                )}

                <SocialProofAnimation socialProof={socialProof} />
              </Box>
            </Flex>

            <ValueProps
              valueProps={valueProps}
              setValuePropsHeight={setValuePropsHeight}
            />
          </Box>
        </Container>
      </Section>
    );
  }),
  { triggerOnce: true }
);

HeroValueProps.displayName = 'HeroValueProps';
HeroValueProps.Schema = Schema;
